import { useState } from 'react';
import { api, errMsg } from '../api/client';
import { Button, Field, Modal, Textarea, useToast } from '../ui';

/**
 * Generic CSV import modal on the universal pipeline: paste → preview (per-line validation with
 * Persian errors) → commit → report. One component, reused by customers / suppliers / sales
 * prices — the same experience everywhere data comes in as a file.
 */

interface PreviewRow {
  line: number;
  name?: string;
  code?: string;
  price?: number | string;
  action: string;
  errors: string[];
}
interface Preview {
  summary: { total: number; create: number; update: number; skip: number };
  rows: PreviewRow[];
}

const ACTION_FA: Record<string, string> = {
  create: 'ایجاد',
  update: 'به‌روزرسانی',
  skip: 'رد',
};

export function CsvImportModal({
  title,
  hint,
  previewUrl,
  commitUrl,
  onClose,
  onDone,
}: {
  title: string;
  hint: string;
  previewUrl: string;
  commitUrl: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);

  async function doPreview() {
    setBusy(true);
    const r = await api<Preview>(previewUrl, { method: 'POST', body: JSON.stringify({ csv }) });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    setPreview(r.body as Preview);
  }

  async function doCommit() {
    setBusy(true);
    const r = await api<{ added: number; updated: number; skipped: number }>(commitUrl, {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    const res = r.body as { added: number; updated: number; skipped: number };
    toast(`${res.added} افزوده، ${res.updated} به‌روزرسانی، ${res.skipped} رد شد`);
    onDone();
  }

  return (
    <Modal title={title} onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        {hint} — سطر عنوان فارسی یا انگلیسی؛ ارقام فارسی و جداکنندهٔ هزارگان پشتیبانی می‌شود.
      </p>
      <Field label="متن CSV (از اکسل کپی کنید)">
        <Textarea rows={7} value={csv} onChange={(e) => setCsv(e.target.value)} />
      </Field>
      {preview ? (
        <>
          <div className="totalbox" style={{ marginTop: 10 }}>
            {preview.summary.total} سطر · {preview.summary.create} ایجاد · {preview.summary.update}{' '}
            به‌روزرسانی · {preview.summary.skip} رد
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 8 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>سطر</th>
                  <th>شرح</th>
                  <th>اقدام</th>
                  <th>خطا</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="num">{r.line}</td>
                    <td className="num">{[r.name, r.code, r.price].filter(Boolean).join(' · ')}</td>
                    <td>{ACTION_FA[r.action] ?? r.action}</td>
                    <td className="warn">{r.errors.join('، ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      <div className="row" style={{ marginTop: 12 }}>
        <Button variant="secondary" onClick={doPreview} loading={busy} disabled={!csv.trim()}>
          پیش‌نمایش
        </Button>
        <Button
          onClick={doCommit}
          loading={busy}
          disabled={!preview || preview.summary.create + preview.summary.update === 0}
        >
          ثبت نهایی
        </Button>
      </div>
    </Modal>
  );
}
